import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import pako from 'pako';
import { prysmDB } from '../utils/database';
import apiService from '../services/apiService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const COOKIE_NAME = process.env.REACT_APP_COOKIE_NAME || 'prysm-registration';
const USER_IMAGE_PATH = BACKEND_URL + process.env.REACT_APP_USER_IMAGE_URL || '/images/users';

// Utility function to format file sizes
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Professional Image Analysis Component
const ImageAnalysisCard = ({ title, imageData, stats, type = 'selfie' }) => {
  if (!imageData) {
    console.log(`ImageAnalysisCard: No imageData for ${title}`);
    return null;
  }

  // Debug logging for image data
  console.log(`ImageAnalysisCard: ${title}`, {
    hasCompressedBase64: !!imageData?.compressedBase64,
    hasFileStats: !!imageData?.fileStats,
    stats: stats
  });

  // Function to decompress base64 data
  const decompressBase64 = (compressedBase64) => {
    try {
      if (!compressedBase64) return null;

      // Convert base64 to Uint8Array
      const binaryString = atob(compressedBase64);
      const compressedData = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        compressedData[i] = binaryString.charCodeAt(i);
      }

      // Decompress using pako
      const decompressedData = pako.inflate(compressedData);

      // Convert back to base64 using chunked approach to avoid call stack issues
      let decompressedBase64;
      try {
        decompressedBase64 = btoa(String.fromCharCode.apply(null, decompressedData));
      } catch (btoaError) {
        console.warn('btoa failed, using chunked approach:', btoaError.message);
        const chunkSize = 8192;
        let binaryString = '';
        for (let i = 0; i < decompressedData.length; i += chunkSize) {
          const chunk = decompressedData.slice(i, i + chunkSize);
          binaryString += String.fromCharCode.apply(null, chunk);
        }
        decompressedBase64 = btoa(binaryString);
      }
      return decompressedBase64;
    } catch (error) {
      console.error(`Failed to decompress data for ${title}:`, error);
      console.error(`Error details:`, {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      return null;
    }
  };

  const getImageSrc = () => {

    // For selfie data - decompress compressedBase64 for display
    if (imageData.compressedBase64) {
      const decompressedBase64 = decompressBase64(imageData.compressedBase64);
      if (decompressedBase64) {
        return `data:image/jpeg;base64,${decompressedBase64}`;
      }
    }

    // For license data - decompress front or back compressed data
    if (imageData.front && imageData.front.compressedBase64) {
      const decompressedBase64 = decompressBase64(imageData.front.compressedBase64);
      if (decompressedBase64) {
        return `data:image/jpeg;base64,${decompressedBase64}`;
      }
    }

    if (imageData.back && imageData.back.compressedBase64) {
      const decompressedBase64 = decompressBase64(imageData.back.compressedBase64);
      if (decompressedBase64) {
        return `data:image/jpeg;base64,${decompressedBase64}`;
      }
    }

    console.log(`No valid image data found for ${title}:`, Object.keys(imageData));
    return null;
  };

  const getStatusColor = () => {
    switch (type) {
      case 'selfie':
        return stats?.faces_detected > 0 ? 'text-green-400' : 'text-red-400';
      case 'license':
        return 'text-blue-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = () => {
    switch (type) {
      case 'selfie':
        return stats?.faces_detected > 0 ? '✓' : '⚠';
      case 'license':
        return '⚠';
      default:
        return '📷';
    }
  };

  const getStatusText = () => {
    switch (type) {
      case 'selfie':
        return stats?.faces_detected > 0 ? 'CAPTURED & VERIFIED' : 'CAPTURED';
      case 'license':
        return 'CAPTURED & PENDING';
      default:
        return 'CAPTURED';
    }
  };

  return (
    <div className="bg-gray-900/80 border border-gray-700 rounded-lg p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-200 flex items-center">
          <span className="mr-2">{getStatusIcon()}</span>
          {title}
        </h3>
        <span className={`text-sm font-medium ${getStatusColor()} flex items-center`}>
          <span className="mr-1">{getStatusIcon()}</span>
          {getStatusText()}
        </span>
      </div>

      {/* Image Display */}
      <div className="mb-4">
        <div className="relative bg-gray-800 rounded-lg overflow-hidden border border-gray-600">
          {(() => {
            const imageSrc = getImageSrc();
            console.log(`Image src for ${title}:`, imageSrc ? `${imageSrc.substring(0, 50)}...` : 'NULL');
            return (
              <img
                src={imageSrc}
                alt={title}
                className="w-full h-48 object-cover"
                onError={(e) => console.error(`Image load error for ${title}:`, e)}
                onLoad={() => console.log(`Image loaded successfully for ${title}`)}
              />
            );
          })()}
          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {type.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Technical Specifications */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
            <div className="text-gray-400 font-medium mb-1">File Size</div>
            <div className="text-gray-200 font-mono">
              {stats?.originalSize ? formatFileSize(stats.originalSize) :
                imageData?.fileStats?.originalSize ? formatFileSize(imageData.fileStats.originalSize) :
                  imageData?.originalFileSize ? formatFileSize(imageData.originalFileSize) : 'N/A'}
            </div>
          </div>
          <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
            <div className="text-gray-400 font-medium mb-1">Compressed</div>
            <div className="text-gray-200 font-mono">
              {stats?.compressedSize ? formatFileSize(stats.compressedSize) :
                imageData?.fileStats?.compressedSize ? formatFileSize(imageData.fileStats.compressedSize) : 'N/A'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
            <div className="text-gray-400 font-medium mb-1">Compression Ratio</div>
            <div className="text-gray-200 font-mono">
              {(() => {
                // Calculate compression ratio from available data
                const originalSize = stats?.originalSize || imageData?.fileStats?.originalSize;
                const compressedSize = stats?.compressedSize || imageData?.fileStats?.compressedSize;

                if (originalSize && compressedSize && originalSize > 0) {
                  const ratio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
                  return `${ratio}%`;
                }

                // Fallback to stored ratio
                return stats?.compressionRatio ? `${stats.compressionRatio.toFixed(1)}%` :
                  imageData?.fileStats?.compressionRatio ? `${imageData.fileStats.compressionRatio.toFixed(1)}%` : 'N/A';
              })()}
            </div>
          </div>
          <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
            <div className="text-gray-400 font-medium mb-1">Format</div>
            <div className="text-gray-200 font-mono uppercase">
              {imageData?.fileType || imageData?.fileStats?.fileType || 'JPEG'}
            </div>
          </div>
        </div>

        {type === 'selfie' && stats && (
          <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
            <div className="text-gray-400 font-medium mb-2">Biometric Analysis</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-400">Faces:</span>
                <span className="text-gray-200 ml-2 font-mono">{stats.faces_detected || 0}</span>
              </div>
              {stats.image_analysis && stats.image_analysis[0] && (
                <>
                  <div>
                    <span className="text-gray-400">Age:</span>
                    <span className="text-gray-200 ml-2 font-mono">{stats.image_analysis[0].age || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Gender:</span>
                    <span className="text-gray-200 ml-2 font-mono">{stats.image_analysis[0].gender || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Confidence:</span>
                    <span className="text-gray-200 ml-2 font-mono">
                      {stats.image_analysis[0].confidence ? `${(stats.image_analysis[0].confidence * 100).toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
          <div className="text-gray-400 font-medium mb-1">Timestamp</div>
          <div className="text-gray-200 font-mono text-xs">
            {imageData.timestamp ? new Date(imageData.timestamp).toLocaleString() : 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Error Modal Component
const ErrorModal = ({ errors, onClose, isVisible }) => {
  if (!isVisible || !errors || errors.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-lg w-full transform transition-all duration-300 scale-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-red-400">Registration Issues</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-3">
          {errors.map((error, index) => (
            <div key={index} className="flex items-start bg-gray-700/50 p-3 rounded-lg">
              <span className="text-red-400 mr-2">•</span>
              <span className="text-gray-200">{error}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
            <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
          </div>
          <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700 relative z-10">
            <div className="text-red-400 text-center">
              <h2 className="text-xl font-semibold mb-4">Something went wrong</h2>
              <p className="text-gray-300 mb-4">We apologize for the inconvenience. Please try refreshing the page.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 w-full shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const Checkout = () => {
  const [userData, setUserData] = useState(null);
  const [selfieData, setSelfieData] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registerStatus, setRegisterStatus] = useState('');
  const [smsVerified, setSmsVerified] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({});
  const [submissionItems, setSubmissionItems] = useState([]);
  const [showProgress, setShowProgress] = useState(false);
  const [submissionReady, setSubmissionReady] = useState(false);
  const [registrationResponseReady, setRegistrationResponseReady] = useState(false);
  const navigate = useNavigate();

  // Status checking functions based on storage data
  const getSelfieStatus = () => {
    if (!selfieData) return { status: 'missing', text: 'NOT UPLOADED', color: 'text-red-400', icon: '⚠' };

    const verificationResult = selfieData.verificationResult;
    if (verificationResult && verificationResult.status === 'processed') {
      return { status: 'processed', text: 'VERIFIED & PROCESSED', color: 'text-green-400', icon: '✓' };
    } else if (verificationResult && verificationResult.status === 'processing') {
      return { status: 'processing', text: 'PROCESSING', color: 'text-yellow-400', icon: '⏳' };
    } else if (selfieData.uploadResponse) {
      return { status: 'uploaded', text: 'UPLOADED', color: 'text-blue-400', icon: '📷' };
    } else {
      return { status: 'incomplete', text: 'INCOMPLETE', color: 'text-red-400', icon: '⚠' };
    }
  };

  const getLicenseFrontStatus = () => {
    if (!licenseData) return { status: 'missing', text: 'NOT UPLOADED', color: 'text-red-400', icon: '⚠' };

    if (licenseData.frontImageId) {
      return { status: 'uploaded', text: 'UPLOADED', color: 'text-green-400', icon: '✓' };
    } else {
      return { status: 'missing', text: 'NOT UPLOADED', color: 'text-red-400', icon: '⚠' };
    }
  };

  const getLicenseBackStatus = () => {
    if (!licenseData) return { status: 'missing', text: 'NOT UPLOADED', color: 'text-red-400', icon: '⚠' };

    if (licenseData.backImageId) {
      return { status: 'uploaded', text: 'UPLOADED', color: 'text-green-400', icon: '✓' };
    } else {
      return { status: 'missing', text: 'NOT UPLOADED', color: 'text-red-400', icon: '⚠' };
    }
  };

  const getPlanSelectionStatus = () => {
    if (!paymentData) return { status: 'missing', text: 'NOT SELECTED', color: 'text-red-400', icon: '⚠' };

    const squareResponse = paymentData.squareResponse;
    if (squareResponse) {
      if (squareResponse.status === 'success' || squareResponse.success === true) {
        return {
          status: 'success',
          text: 'PAID & CONFIRMED',
          color: 'text-green-400',
          icon: '✓',
          planName: squareResponse.planName || paymentData.selectedPlan || 'Unknown Plan'
        };
      } else if (squareResponse.status === 'pending' || squareResponse.success === false) {
        return {
          status: 'pending',
          text: 'PENDING',
          color: 'text-yellow-400',
          icon: '⏳',
          planName: squareResponse.planName || paymentData.selectedPlan || 'Unknown Plan'
        };
      } else {
        return {
          status: 'failed',
          text: 'PAYMENT FAILED',
          color: 'text-red-400',
          icon: '⚠',
          planName: squareResponse.planName || paymentData.selectedPlan || 'Unknown Plan'
        };
      }
    } else {
      return {
        status: 'incomplete',
        text: 'INCOMPLETE',
        color: 'text-red-400',
        icon: '⚠',
        planName: paymentData.selectedPlan || 'Unknown Plan'
      };
    }
  };

  // Validation function to check if all requirements are met
  const isSubmissionReady = async () => {
    // Check license front and back have been submitted
    const licenseFrontStatus = getLicenseFrontStatus();
    const licenseBackStatus = getLicenseBackStatus();
    const licenseReady = licenseFrontStatus.status === 'uploaded' && licenseBackStatus.status === 'uploaded';

    // Check selfie has been submitted
    const selfieStatus = getSelfieStatus();
    const selfieReady = selfieStatus.status === 'processed' || selfieStatus.status === 'uploaded';

    // Check payment result is success
    const paymentStatus = getPlanSelectionStatus();
    const paymentReady = paymentStatus.status === 'success';

    // Check registration response is success (using same logic as registration page)
    let registrationReady = false;
    try {
      const registrationResponse = await prysmDB.get('registration_data', 'registration_response');
      registrationReady = registrationResponse && registrationResponse.status === 'success';
    } catch (error) {
      console.error('[Checkout] Error checking registration response:', error);
      registrationReady = false;
    }

    console.log('[Checkout] Validation status:', {
      licenseReady,
      selfieReady,
      paymentReady,
      registrationReady,
      licenseFront: licenseFrontStatus.status,
      licenseBack: licenseBackStatus.status,
      selfie: selfieStatus.status,
      payment: paymentStatus.status,
      registrationResponseStatus: registrationReady ? 'success' : 'not_found_or_failed'
    });

    return licenseReady && selfieReady && paymentReady && registrationReady;
  };

  // Clear all old data stores
  const clearOldDataStores = async () => {
    try {
      console.log('Clearing all old data stores...');

      // Clear all sessionStorage
      sessionStorage.clear();
      console.log('Cleared sessionStorage');

      // Clear deprecated tokens from localStorage
      const deprecatedTokens = [
        'op-girlsnight',
        'ourprysm_session',
        'op-girlsnight_session',
        'prysm_session'
      ];

      let cleanedCount = 0;
      deprecatedTokens.forEach(token => {
        if (localStorage.getItem(token)) {
          localStorage.removeItem(token);
          cleanedCount++;
          console.log(`[Checkout] Removed deprecated localStorage token: ${token}`);
        }
      });

      if (cleanedCount > 0) {
        console.log(`[Checkout] Cleaned up ${cleanedCount} deprecated tokens from localStorage`);
      }

      // Clear deprecated tokens from storage
      const deprecatedstorageTokens = [
        'op-girlsnight',
        'ourprysm_session',
        'op-girlsnight_session',
        'prysm_session'
      ];

      for (const token of deprecatedstorageTokens) {
        try {
          await prysmDB.delete('registration_data', token);
          cleanedCount++;
          console.log(`[Checkout] Removed deprecated storage token: ${token}`);
        } catch (error) {
          // Token might not exist, which is fine
          console.log(`[Checkout] Token ${token} not found in storage (expected)`);
        }
      }

      console.log('Data store cleanup complete');
    } catch (error) {
      console.warn('Error clearing data stores:', error);
    }
  };

  // Load data from storage only
  const loadSessionData = async () => {
    try {
      // Clear old data stores first
      await clearOldDataStores();

      // Load registration data from storage
      const registrationData = await prysmDB.get('registration_data', 'current_registration');
      if (registrationData) {
        setSessionData(registrationData);
        console.log('[Checkout] Loaded registration data from storage');
      }

      // Load selfie data from storage
      const selfieData = await prysmDB.get('selfie_data', 'current_selfie');
      if (selfieData) {
        setSelfieData(selfieData);
        console.log('[Checkout] Loaded selfie data from storage');
      }

      // Load license data from storage
      const licenseData = await prysmDB.get('license_data', 'current_license');
      if (licenseData) {
        setLicenseData(licenseData);
        console.log('[Checkout] Loaded license data from storage');
      }

      // Load payment data from storage
      const paymentData = await prysmDB.get('payment_result', 'current_payment');
      if (paymentData) {
        setPaymentData(paymentData);
        console.log('[Checkout] Loaded payment data from storage');
      }

      // Debug: List all storage items
      console.log('[Checkout] All localStorage items:', Object.keys(localStorage));
      console.log('[Checkout] All sessionStorage items (should be empty):', Object.keys(sessionStorage));
    } catch (error) {
      console.error('[Checkout] Error loading session data:', error);
    }
  };


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load storage data first
        await loadSessionData();

        let jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
        if (!jwt) {
          // Try to reset session and get new token
          if (window.resetSession) {
            await window.resetSession();
            jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
            if (!jwt) {
              navigate('/register');
              return;
            }
          } else {
            navigate('/register');
            return;
          }
        }

        // Since nothing has been submitted to server yet, browser data is the source of truth
        // No need to fetch from server - all data comes from localStorage
        console.log('Using browser data as source of truth - no server fetch needed');

        // Load SMS verification status from browser data
        loadSmsVerificationStatus();

        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError(err.message || 'Failed to load profile data');
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // SMS verification status comes from browser data, not server
  const loadSmsVerificationStatus = async () => {
    try {
      // Check storage for SMS verification status
      const smsResult = await prysmDB.get('sms_send_result', 'current_sms');
      if (smsResult) {
        console.log('[Checkout] Loaded SMS verification status from storage:', smsResult);
        setSmsVerified(smsResult.verified || false);
      } else {
        console.log('[Checkout] No SMS verification data found in storage');
        setSmsVerified(false);
      }
    } catch (error) {
      console.error('[Checkout] Error loading SMS verification status from storage:', error);
      setSmsVerified(false);
    }
  };

  // Get visitor UUID from storage
  const getVisitorUUID = async () => {
    try {
      const uuidData = await prysmDB.get('visitorUUID', 'current_uuid');
      return uuidData ? uuidData.uuid : null;
    } catch (error) {
      console.error('[Checkout] Error getting visitor UUID:', error);
      return null;
    }
  };

  // Prepare submission items
  const prepareSubmissionItems = async () => {
    const items = [];
    const visitorUUID = await getVisitorUUID();

    // Registration data
    if (sessionData || userData) {
      items.push({
        id: 'registration_data',
        name: 'Personal Information',
        data: sessionData || userData,
        dataType: 'registration_data'
      });
    }

    // Selfie data
    if (selfieData) {
      items.push({
        id: 'selfie_data',
        name: 'Facial Recognition Data',
        data: selfieData,
        dataType: 'selfie_data'
      });
    }

    // License front data
    if (licenseData?.front) {
      items.push({
        id: 'license_front',
        name: 'License - Front Image',
        data: licenseData.front,
        dataType: 'license_front'
      });
    }

    // License back data
    if (licenseData?.back) {
      items.push({
        id: 'license_back',
        name: 'License - Back Image',
        data: licenseData.back,
        dataType: 'license_back'
      });
    }

    // Payment data from storage
    try {
      const paymentResult = await prysmDB.get('payment_result', 'current_payment');
      if (paymentResult) {
        console.log('[Checkout] Payment result found in storage:', paymentResult);
        items.push({
          id: 'payment_data',
          name: 'Payment Information',
          data: paymentResult,
          dataType: 'payment_data'
        });
      } else {
        console.log('[Checkout] No payment result found in storage');
      }
    } catch (error) {
      console.error('[Checkout] Error retrieving payment data from storage:', error);
      // Continue without payment data rather than failing the entire submission
    }

    return items;
  };

  // Submit individual item with retry logic
  const submitItem = async (item, jwt, maxRetries = 3) => {
    const visitorUUID = getVisitorUUID();
    if (!visitorUUID) {
      throw new Error('Visitor UUID not found');
    }

    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Update progress
        setSubmissionProgress(prev => ({
          ...prev,
          [item.id]: { status: 'sending', attempt, maxRetries }
        }));

        const response = await apiService.submitUserData({
          visitorUUID,
          dataType: item.dataType,
          data: item.data
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Success
          setSubmissionProgress(prev => ({
            ...prev,
            [item.id]: { status: 'success', attempt }
          }));
          return { success: true, data };
        } else {
          throw new Error(data.error?.message || `HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        lastError = error;
        console.error(`Attempt ${attempt} failed for ${item.id}:`, error);

        // Update progress with error
        setSubmissionProgress(prev => ({
          ...prev,
          [item.id]: {
            status: 'error',
            attempt,
            maxRetries,
            error: error.message
          }
        }));

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All attempts failed
    setSubmissionProgress(prev => ({
      ...prev,
      [item.id]: {
        status: 'failed',
        attempt: maxRetries,
        maxRetries,
        error: lastError?.message || 'Unknown error'
      }
    }));

    return { success: false, error: lastError };
  };

  const handleRegister = async () => {
    try {
      setError(null);
      setIsSubmitting(true);
      setShowProgress(true);
      setSubmissionProgress({});

      let jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
      if (!jwt) {
        // Try to reset session and get new token
        if (window.resetSession) {
          await window.resetSession();
          jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
          if (!jwt) {
            setError(['Authentication required. Please log in again.']);
            setIsSubmitting(false);
            setShowProgress(false);
            return;
          }
        } else {
          setError(['Authentication required. Please log in again.']);
          setIsSubmitting(false);
          setShowProgress(false);
          return;
        }
      }

      // Prepare submission items
      const items = await prepareSubmissionItems();
      setSubmissionItems(items);

      if (items.length === 0) {
        setError(['No data available for submission.']);
        setIsSubmitting(false);
        setShowProgress(false);
        return;
      }

      // Submit each item
      const results = [];
      for (const item of items) {
        const result = await submitItem(item, jwt);
        results.push({ item, result });
      }

      // Check if all items succeeded
      const allSuccessful = results.every(({ result }) => result.success);

      if (allSuccessful) {
        // All successful - navigate to thank you
        setTimeout(() => {
          navigate('/thank-you');
        }, 2000);
      } else {
        // Some failed
        const failedItems = results.filter(({ result }) => !result.success);
        setError([`${failedItems.length} of ${items.length} data items failed to submit. Please review the errors below.`]);
      }

    } catch (err) {
      console.error('Submission error:', err);
      setError([err.message || 'Failed to submit data. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add effect to show error modal when errors occur
  useEffect(() => {
    if (error && Array.isArray(error)) {
      setShowErrorModal(true);
    }
  }, [error]);

  // Check registration response status
  useEffect(() => {
    const checkRegistrationResponse = async () => {
      try {
        const registrationResponse = await prysmDB.get('registration_data', 'registration_response');
        const ready = registrationResponse && registrationResponse.status === 'success';
        setRegistrationResponseReady(ready);
      } catch (error) {
        console.error('[Checkout] Error checking registration response:', error);
        setRegistrationResponseReady(false);
      }
    };

    checkRegistrationResponse();
  }, [sessionData]);

  // Check submission readiness whenever data changes
  useEffect(() => {
    const checkSubmissionReadiness = async () => {
      try {
        const ready = await isSubmissionReady();
        setSubmissionReady(ready);
      } catch (error) {
        console.error('[Checkout] Error checking submission readiness:', error);
        setSubmissionReady(false);
      }
    };

    checkSubmissionReadiness();
  }, [userData, selfieData, licenseData, paymentData, registrationResponseReady]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        <div className="relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }

  if (error && !Array.isArray(error)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700 relative z-10">
          <div className="text-red-400 text-center">
            <p className="text-lg font-semibold">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 w-full shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="w-full max-w-4xl relative z-10">
          <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 mb-4 animate-fade-in">
                VERIFICATION DASHBOARD
              </h1>
              <div className="h-1 w-32 bg-gradient-to-r from-green-500 to-blue-600 mx-auto rounded-full animate-glow"></div>
              <p className="mt-4 text-gray-300 max-w-3xl mx-auto font-mono text-sm">
                CLASSIFICATION: CONFIDENTIAL | AUTHENTICATION REQUIRED<br />
                All biometric and document verification data has been furnished and ready for upload.<br />
                Proceed with final registration submission to begin verification process.
              </p>
            </div>

            {/* Personal Information Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-300 mb-6 flex items-center">
                <span className="mr-3">👤</span>
                Personal Information
              </h2>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Full Name</label>
                    <p className="text-lg text-gray-200 font-mono">
                      {sessionData?.first_name || userData?.first_name || 'N/A'} {sessionData?.last_name || userData?.last_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.email || userData?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Facebook Profile</label>
                    <a
                      href={sessionData?.facebook_url || userData?.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-blue-400 hover:text-blue-300 transition-colors font-mono break-all"
                    >
                      {sessionData?.facebook_url || userData?.facebook_url || 'N/A'}
                    </a>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Mobile Number</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.mobile_number || userData?.mobile_number || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Date of Birth</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.birthday || userData?.birthday || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Zip Code</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.zip_code || userData?.zip_code || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Relationship Status</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.relationship_status || userData?.relationship_status || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Gender</label>
                    <p className="text-lg text-gray-200 font-mono">{sessionData?.gender || userData?.gender || 'N/A'}</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Uploaded Files Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-300 mb-6 flex items-center">
                <span className="mr-3">📁</span>
                Uploaded Files
              </h2>

              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
                <div className="space-y-4">
                  {/* Selfie File */}
                  {(() => {
                    const selfieStatus = getSelfieStatus();
                    const isRequired = selfieStatus.status !== 'processed' && selfieStatus.status !== 'uploaded';
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${isRequired ? 'bg-red-900/20 border-red-600' : 'bg-gray-800/50 border-gray-600'
                        }`}>
                        <div className="flex items-center">
                          <span className={`${selfieStatus.color} mr-3 text-xl`}>{selfieStatus.icon}</span>
                          <span className="text-gray-200 font-medium">
                            Selfie Image
                            {isRequired && <span className="text-red-400 text-xs ml-2">(REQUIRED)</span>}
                          </span>
                        </div>
                        <span className={`${selfieStatus.color} text-sm font-medium`}>{selfieStatus.text}</span>
                      </div>
                    );
                  })()}

                  {/* License Front File */}
                  {(() => {
                    const frontStatus = getLicenseFrontStatus();
                    const isRequired = frontStatus.status !== 'uploaded';
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${isRequired ? 'bg-red-900/20 border-red-600' : 'bg-gray-800/50 border-gray-600'
                        }`}>
                        <div className="flex items-center">
                          <span className={`${frontStatus.color} mr-3 text-xl`}>{frontStatus.icon}</span>
                          <span className="text-gray-200 font-medium">
                            Driver's License - Front
                            {isRequired && <span className="text-red-400 text-xs ml-2">(REQUIRED)</span>}
                          </span>
                        </div>
                        <span className={`${frontStatus.color} text-sm font-medium`}>{frontStatus.text}</span>
                      </div>
                    );
                  })()}

                  {/* License Back File */}
                  {(() => {
                    const backStatus = getLicenseBackStatus();
                    const isRequired = backStatus.status !== 'uploaded';
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${isRequired ? 'bg-red-900/20 border-red-600' : 'bg-gray-800/50 border-gray-600'
                        }`}>
                        <div className="flex items-center">
                          <span className={`${backStatus.color} mr-3 text-xl`}>{backStatus.icon}</span>
                          <span className="text-gray-200 font-medium">
                            Driver's License - Back
                            {isRequired && <span className="text-red-400 text-xs ml-2">(REQUIRED)</span>}
                          </span>
                        </div>
                        <span className={`${backStatus.color} text-sm font-medium`}>{backStatus.text}</span>
                      </div>
                    );
                  })()}

                  {/* Plan Selection */}
                  {(() => {
                    const planStatus = getPlanSelectionStatus();
                    const isRequired = planStatus.status !== 'success';
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${isRequired ? 'bg-red-900/20 border-red-600' : 'bg-gray-800/50 border-gray-600'
                        }`}>
                        <div className="flex items-center">
                          <span className={`${planStatus.color} mr-3 text-xl`}>{planStatus.icon}</span>
                          <div className="flex flex-col">
                            <span className="text-gray-200 font-medium">
                              Payment Status
                              {isRequired && <span className="text-red-400 text-xs ml-2">(REQUIRED)</span>}
                            </span>
                            {planStatus.planName && (
                              <span className="text-gray-400 text-xs">{planStatus.planName}</span>
                            )}
                          </div>
                        </div>
                        <span className={`${planStatus.color} text-sm font-medium`}>{planStatus.text}</span>
                      </div>
                    );
                  })()}

                  {/* Registration Response */}
                  {(() => {
                    // Use the registration response ready state
                    const registrationReady = registrationResponseReady;
                    const isRequired = !registrationReady;
                    const statusColor = registrationReady ? 'text-green-400' : 'text-red-400';
                    const statusIcon = registrationReady ? '✓' : '⚠';
                    const statusText = registrationReady ? 'SUCCESS' : 'NOT VERIFIED';

                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border ${isRequired ? 'bg-red-900/20 border-red-600' : 'bg-gray-800/50 border-gray-600'
                        }`}>
                        <div className="flex items-center">
                          <span className={`${statusColor} mr-3 text-xl`}>{statusIcon}</span>
                          <div className="flex flex-col">
                            <span className="text-gray-200 font-medium">
                              Registration Response
                              {isRequired && <span className="text-red-400 text-xs ml-2">(REQUIRED)</span>}
                            </span>
                            <span className="text-gray-400 text-xs">User data verification</span>
                          </div>
                        </div>
                        <span className={`${statusColor} text-sm font-medium`}>{statusText}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Ready for Submission */}
            <div className="mt-8 text-center">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-300 mb-2">READY FOR SUBMISSION</h3>
                <p className="text-sm text-gray-400 font-mono mb-2">
                  Review all data, correct as needed before submission
                </p>
                <p className="text-sm text-gray-400 font-mono">
                  Pending Verification using OurPrysm Verification Protocols - Ready for Secure Transmission
                </p>
              </div>

              {/* Submission Progress */}
              {showProgress && submissionItems.length > 0 && (
                <div className="mb-6 bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-4">Transmission Progress</h4>
                  <div className="space-y-2">
                    {submissionItems.map((item) => {
                      const progress = submissionProgress[item.id];
                      return (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded border border-gray-600">
                          <span className="text-sm text-gray-200 font-mono">{item.name}</span>
                          <div className="flex items-center">
                            {progress?.status === 'sending' && (
                              <span className="text-yellow-400 text-sm">
                                Sending... ({progress.attempt}/{progress.maxRetries})
                              </span>
                            )}
                            {progress?.status === 'success' && (
                              <span className="text-green-400 text-sm flex items-center">
                                <span className="mr-1">✓</span> Transmitted
                              </span>
                            )}
                            {progress?.status === 'error' && (
                              <span className="text-yellow-400 text-sm">
                                Retrying... ({progress.attempt}/{progress.maxRetries})
                              </span>
                            )}
                            {progress?.status === 'failed' && (
                              <span className="text-red-400 text-sm flex items-center">
                                <span className="mr-1">✗</span> Failed: {progress.error}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Requirements Status */}
                {!submissionReady && (
                  <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mb-4">
                    <h4 className="text-yellow-400 font-semibold mb-2">⚠️ Requirements Not Met</h4>
                    <div className="space-y-1 text-sm text-yellow-200">
                      {(() => {
                        const licenseFrontStatus = getLicenseFrontStatus();
                        const licenseBackStatus = getLicenseBackStatus();
                        const selfieStatus = getSelfieStatus();
                        const paymentStatus = getPlanSelectionStatus();
                        // Use the registration response ready state
                        const registrationReady = registrationResponseReady;

                        const missingRequirements = [];

                        if (licenseFrontStatus.status !== 'uploaded') {
                          missingRequirements.push('• License front image must be uploaded');
                        }
                        if (licenseBackStatus.status !== 'uploaded') {
                          missingRequirements.push('• License back image must be uploaded');
                        }
                        if (selfieStatus.status !== 'processed' && selfieStatus.status !== 'uploaded') {
                          missingRequirements.push('• Selfie image must be uploaded and verified');
                        }
                        if (paymentStatus.status !== 'success') {
                          missingRequirements.push('• Payment must be successfully processed');
                        }
                        if (!registrationReady) {
                          missingRequirements.push('• Registration response must be successful');
                        }

                        return missingRequirements.map((req, index) => (
                          <div key={index}>{req}</div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleRegister}
                  disabled={isSubmitting || !submissionReady}
                  className={`relative px-8 py-4 text-white rounded-xl transform transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-lg font-semibold tracking-wide overflow-hidden w-full ${submissionReady && !isSubmitting
                    ? 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 hover:scale-[1.02] focus:ring-green-500 cursor-pointer'
                    : 'bg-gray-600 cursor-not-allowed opacity-50'
                    }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      TRANSMITTING...
                    </div>
                  ) : submissionReady ? (
                    'SUBMIT REGISTRATION'
                  ) : (
                    'COMPLETE REQUIREMENTS FIRST'
                  )}

                  {/* Scanner Effect */}
                  {isSubmitting && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-scanner"></div>
                  )}
                </button>

              </div>
              {registerStatus && (
                <p className="mt-4 text-green-400">{registerStatus}</p>
              )}
              {error && Array.isArray(error) && (
                <ErrorModal
                  errors={error}
                  onClose={() => {
                    setShowErrorModal(false);
                    setError(null); // Also clear the errors when closing
                  }}
                  isVisible={showErrorModal}
                />
              )}
            </div>
          </div>
        </div>

        <style jsx="true">{`
          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }
          .animate-blob {
            animation: blob 7s infinite;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
          @keyframes glow {
            0% {
              box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
            }
            50% {
              box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
            }
            100% {
              box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
            }
          }
          .animate-glow {
            animation: glow 2s infinite;
          }
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fade-in {
            animation: fade-in 1s ease-out;
          }
          @keyframes scanner {
            0% {
              transform: translateX(-100%) skewX(-12deg);
            }
            100% {
              transform: translateX(100%) skewX(-12deg);
            }
          }
          .animate-scanner {
            animation: scanner 2s linear infinite;
          }
        `}</style>
      </div>
    </ErrorBoundary>
  );
};

export default Checkout;
